import React, { useEffect, useState } from 'react';
import { Button, Text, View } from "../Components/Themed"
import type { BracketCompetitionProps, CompanyEmbedProps, CompanyPlayerProps, CompanyPlayerStatProps, CompanyProps, CompetitionProps, EmbedPropertyProps, EmbedProps, ExternalKeyProps, MyPlayerProps, PlayerReferralProps, PollCampaignProps, PromoProps, PublicPlayerProps, SquaresCompetitionProps } from '../types';
import type { CompanyMemberProps } from '../Authenticator/api/types';
import { PartnerPortalApi } from './api';
import { FlatList, Image, Platform, ScrollView } from 'react-native';
import { useColors } from '../constants/useColors';
import ImageUploader from '../Components/ImageUploader';
import moment from 'moment-mini';
import { Icons, SearchBox } from '../Components';
import Pagination from '../Components/Pagination';

type PartnerPortalProps = {
    me:MyPlayerProps,
    company_id:string,
    refresh_key?:string,
    onCreateEmbed:() => void,
    onManageReferral: (pr?:PlayerReferralProps) => void,
    onCreateEngagement: (init_engagement?:string) => void,
    onSelectCompanyEmbed: (company_embed:CompanyEmbedProps) => void,
    onSelectCompetition: (competition:CompetitionProps) => void,
    onSelectBracketCompetition:(bracket_competition:BracketCompetitionProps) => void,
    onSelectSquaresCompetition: (squares_competition:SquaresCompetitionProps) => void,
    onSelectFlashCampaign: (flash_campaign:PollCampaignProps) => void
}

const sections = ['top_row','stats','referrals','second_row','third_row']

const PartnerPortal = ({ me, company_id, refresh_key, onCreateEngagement, onSelectBracketCompetition, onManageReferral, onCreateEmbed, onSelectCompanyEmbed, onSelectCompetition, onSelectFlashCampaign, onSelectSquaresCompetition }:PartnerPortalProps) => {
    const Colors = useColors();
    const [ engagement_state, setEngagementState ] = useState<{
        loading: boolean,
        competitions:CompetitionProps[],
        squares_competitions:SquaresCompetitionProps[],
        bracket_competitions:BracketCompetitionProps[],
        flash_campaigns:PollCampaignProps[],
        embeds:EmbedProps[],
        embed_properties:EmbedPropertyProps[],
        company_embeds:CompanyEmbedProps[]
    }>({
        loading:false,
        competitions: [],
        squares_competitions: [],
        bracket_competitions: [],
        flash_campaigns:[],
        embeds:[],
        embed_properties: [],
        company_embeds:[]
    });
    const { loading, competitions, squares_competitions, bracket_competitions, flash_campaigns, embeds, company_embeds } = engagement_state;
    const [ state, setState ] = useState<{
        loading: boolean,
        company?:CompanyProps,
        my_member?:CompanyMemberProps,
        company_player_stats:CompanyPlayerStatProps[],
        company_players:CompanyPlayerProps[],
        company_player_local_offset:number,
        company_players_offset:number,
        player_referrals:PlayerReferralProps[],
        promos:PromoProps[],
        company_members:CompanyMemberProps[],
        external_keys:ExternalKeyProps[],
        players:PublicPlayerProps[],
    }>({
        loading:false,
        company_members: [],
        company_player_local_offset: 0,
        company_players_offset: 0,
        external_keys: [],
        player_referrals:[],
        company_player_stats:[],
        company_players:[],
        promos:[],
        players: []
    });
    const [ referral_search, setReferralSearch ] = useState('');
    const [ player_search, setPlayerSearch ] = useState('');
    const { company, promos, company_players_offset, company_player_local_offset, company_player_stats, company_players, player_referrals, external_keys, players, company_members } = state;
    const filtered_referrals = player_referrals.filter(r => r.referral_code.toLowerCase().includes(referral_search.toLowerCase())).slice(0,company_player_local_offset + 5);
    //we show 5 - so we need to now the pagination of local players
    const filtered_players = company_players.sort((a,b) => b.revenue - a.revenue).filter(p => p.username.toLowerCase().includes(player_search.toLowerCase())).slice(
        company_player_local_offset * 5,
        (company_player_local_offset + 1) * 5
    );
    useEffect(() => {
        PartnerPortalApi.setEnvironment();
        getStateData();
        getActiveEngagementData();
    },[me.player_id, company_id, refresh_key]);

    const getStateData = async() => {
        setState({ ...state, loading:true });
        //First get company data
        const company_response = await PartnerPortalApi.CompanyApi.getCompanyById(company_id);
        let player_ids = company_response ? company_response.company_members.map(cm => cm.player_id) : []
        const s_playes = await PartnerPortalApi.getPlayersByPlayerIds(player_ids);
        const my_co_member = company_response?.company_members.find(cm => cm.player_id == me.player_id);
        const s_keys = await PartnerPortalApi.CompanyApi.getApiKeys(company_id);
        const s_refer = await PartnerPortalApi.CompanyApi.getReferralCodesByCompanyId(company_id);
        const s_stats = await PartnerPortalApi.CompanyApi.getCompanyPlayerStats(company_id);
        const s_cps = await PartnerPortalApi.CompanyApi.getCompanyPlayers(company_id, 0);
        setState({
            ...state,
            loading: false,
            company: company_response?.company,
            external_keys: s_keys,
            promos: s_refer.promos,
            company_players: s_cps,
            player_referrals: s_refer.player_referrals,
            company_player_stats: s_stats,
            players: s_playes,
            company_players_offset: 0,
            my_member:my_co_member,
            company_members: company_response?.company_members ?? []
        });
    }
    const getAdditionalPlayers = async(page:number, local_offset:number) => {
        const s_cps = await PartnerPortalApi.CompanyApi.getCompanyPlayers(company_id, page);
        setState({
            ...state,
            company_players: [ ...company_players, ...s_cps ],
            company_player_local_offset: local_offset,
            company_players_offset: page
        })
    }

    // NEXT
    const handleNext = async() => {
          const nextLocal = company_player_local_offset + 1;
            const startIndex = nextLocal * 5;
            const endIndex = startIndex + 5;
        if (endIndex <= company_players.length) {
            // still have cached records → just advance local offset
            setState({ ...state, company_player_local_offset: nextLocal });
        } else {
            // need more from server → fetch next server page
            await getAdditionalPlayers(company_players_offset + 1, nextLocal)
        }
    };

    // PREV
    const handlePrev = () => {
        if (company_player_local_offset > 0) {
            setState({ ...state, company_player_local_offset: company_player_local_offset - 1 });
        }
    };

    const getActiveEngagementData = async() => {
        setEngagementState({ ...engagement_state, loading:true });
        //Now lets grab our engagements
        const s_comps = await PartnerPortalApi.EngagementApi.getActiveCompetitions(company_id);
        const s_squares = await PartnerPortalApi.EngagementApi.getActiveSquaresCompetitions(company_id);
        const s_brackets = await PartnerPortalApi.EngagementApi.getActiveBracketCompetitions(company_id);

        const s_embeds = await PartnerPortalApi.CompanyEmbedApi.getEmbedsByCompanyId(company_id, 0);
        const s_flash = await PartnerPortalApi.EngagementApi.getActiveFlashCampaigns(company_id);
        setEngagementState({
            ...engagement_state,
            loading:false,
            competitions: s_comps.competitions,
            squares_competitions: s_squares,
            bracket_competitions: s_brackets,
            company_embeds: s_embeds.company_embeds,
            embeds:s_embeds.embeds,
            flash_campaigns: s_flash,
            embed_properties:s_embeds.embed_properties
        })
    }

    const renderCompetitions = (data:{ item:CompetitionProps, index:number }) => {
        return (
            <Button 
                transparent 
                style={{ padding:10, borderRadius:0, flexDirection:'row', alignItems:'center', borderBottomWidth:1, borderColor:Colors.borders.light }}
                onPress={() => onSelectCompetition(data.item)}>
                <Image
                    source={{ uri: data.item.image?.url }}
                    style={{ height:35, width:35, borderRadius:4 }}
                    resizeMode='cover'
                />
                <View transparent style={{ flex:1, marginLeft:10 }}>
                    <Text theme='h1'>{data.item.competition_name}</Text>
                    <Text theme='description' style={{ marginTop:3 }}>{data.item.competition_description}</Text>
                </View>
            </Button>
        )
    }

    const renderSquares = (data:{ item:SquaresCompetitionProps, index:number }) => {
        return (
            <Button 
                transparent 
                style={{ padding:10, borderRadius:0, flexDirection:'row', alignItems:'center', borderBottomWidth:1, borderColor:Colors.borders.light }}
                onPress={() => onSelectSquaresCompetition(data.item)}>
                <Image
                    source={{ uri: data.item.image?.url }}
                    style={{ height:35, width:35, borderRadius:4 }}
                    resizeMode='cover'
                />
                <View transparent style={{ flex:1, marginLeft:10 }}>
                    <Text theme='h1'>{data.item.sq_comp_name}</Text>
                    <Text theme='description' style={{ marginTop:3 }}>{data.item.sq_comp_description}</Text>
                </View>
            </Button>
        )
    }

    const renderBrackets = (data:{ item:BracketCompetitionProps, index:number }) => {
        return (
            <Button 
                transparent 
                style={{ padding:10, borderRadius:0, flexDirection:'row', alignItems:'center', borderBottomWidth:1, borderColor:Colors.borders.light }}
                onPress={() => onSelectBracketCompetition(data.item)}>
                <Image
                    source={{ uri: data.item.image?.url }}
                    style={{ height:35, width:35, borderRadius:4 }}
                    resizeMode='cover'
                />
                <View transparent style={{ flex:1, marginLeft:10 }}>
                    <Text theme='h1'>{data.item.competition_name}</Text>
                    <Text theme='description' style={{ marginTop:3 }}>{data.item.competition_description}</Text>
                </View>
            </Button>
        )
    }

        const renderKeys = (data:{ item:ExternalKeyProps, index:number }) => {
        return (
            <Button 
                transparent 
                style={{ padding:10, borderRadius:0, flexDirection:'row', alignItems:'center', borderBottomWidth:1, borderColor:Colors.borders.light }}
                onPress={() => console.log(data.item)}>
                <Icons.LinkIcon size={40} color={Colors.text.h1} />
                <View transparent style={{ flex:1, marginLeft:10 }}>
                    <Text theme='h2'>{data.item.key}</Text>
                    <Text theme='description' style={{ marginTop:3 }}>{data.item.key_source}</Text>
                </View>
            </Button>
        )
    }

    const renderFlashCampaigns = (data:{ item:PollCampaignProps, index:number }) => {
        return (
            <Button 
                transparent 
                style={{ padding:10, borderRadius:0, flexDirection:'row', alignItems:'center', borderBottomWidth:1, borderColor:Colors.borders.light }}
                onPress={() => onSelectFlashCampaign(data.item)}>
                <Image
                    source={{ uri: data.item.campaign_image?.url }}
                    style={{ height:35, width:35, borderRadius:4 }}
                    resizeMode='cover'
                />
                <View transparent style={{ flex:1, marginLeft:10 }}>
                    <Text theme='h1'>{data.item.name}</Text>
                    <Text theme='description' style={{ marginTop:3 }}>Flash markets</Text>
                </View>
            </Button>
        )
    }

    const renderEmbeds = (data:{ item:CompanyEmbedProps, index:number }) => {
        const embed = embeds.find(e => e.embed_id == data.item.embed_id);
        if(!embed){ return <></> }
        return (
            <Button 
                transparent 
                style={{ padding:10, borderRadius:0, flexDirection:'row', alignItems:'center', borderBottomWidth:1, borderColor:Colors.borders.light }}
                onPress={() => onSelectCompanyEmbed(data.item)}>
                <Icons.EmbedIcon size={24} color={Colors.text.h1} />
                <View transparent style={{ flex:1, marginLeft:10 }}>
                    <Text theme='h1'>{data.item.name}</Text>
                    <Text theme='description' style={{ marginTop:3 }}>{embed.name}</Text>
                </View>
                <Text theme='description'>{data.item.status.toUpperCase()}</Text>
            </Button>
        )
    }

    const renderPlayers = (data:{ item:CompanyPlayerProps, index:number }) => {
        return (
            <View transparent type='row' style={{ padding:10, margin:4, minWidth:300, flexGrow:1, borderBottomWidth:1, borderColor:Colors.borders.light }}>
                <Image
                    source={{ uri: data.item.profile_pic }}
                    style={{ height:40, width:40, borderRadius:100 }}
                    resizeMode='cover'
                />
                <View float style={{ marginRight:10, marginLeft:10, padding:5, borderRadius:22, backgroundColor:data.item.vouched_status == 'verified' ? Colors.text.success : data.item.vouched_status == 'unverified' ? Colors.text.action : Colors.text.error }}>
                    <Text theme='description' color={Colors.text.white}>{data.item.vouched_status.toUpperCase()}</Text>
                </View>
                <View transparent style={{ flex:1, minWidth:200, marginLeft:10 }}>
                    <Text theme='h1'>{data.item.username}</Text>
                    <Text theme='description' style={{ marginTop:3 }}>Used Code {data.item.code_used}</Text>
                </View>
                <View float style={{ marginRight:25, padding:5, borderRadius:22, backgroundColor:data.item.verified ? Colors.text.success : Colors.text.error }}>
                    <Text theme='description' color={Colors.text.white}>Email</Text>
                </View>
                <View float style={{ marginRight:25, padding:5, borderRadius:22, backgroundColor:data.item.phone_verified ? Colors.text.success : Colors.text.error }}>
                    <Text theme='description' color={Colors.text.white}>Phone</Text>
                </View>
                <View transparent style={{ alignItems:'flex-end' }}>
                    <Text theme='h1' color={Colors.text.success}>${data.item.revenue.toFixed(2)}</Text>
                    <Text theme='description' style={{ marginTop:3 }}>Revenue Generated</Text>
                </View>
            </View>
        )
    }

    const renderReferrals = (data:{ item:PlayerReferralProps, index:number }) => {
        const promo = promos.find(e => e.promo_id == data.item.promo_id);
        if(!promo){ return <></> }
        const stats = company_player_stats.filter(ps => ps.referral_code.toLowerCase() == data.item.referral_code.toLowerCase());
        const players_generated = stats.reduce((a,b) => a + b.player_count, 0)
        return (
            <Button 
                transparent 
                style={{ padding:10, borderRadius:0, flexDirection:'row', alignItems:'center', borderBottomWidth:1, borderColor:Colors.borders.light }}
                onPress={() => onManageReferral(data.item)}>
                <View transparent style={{ flex:1, marginLeft:10 }}>
                    <Text theme='h1'>{data.item.referral_code}</Text>
                    <Text theme='description' style={{ marginTop:3 }}>{data.item.status == 'pending' ? 'ACTIVE':  data.item.status.toUpperCase()} - {promo.description}</Text>
                </View>
                <View transparent style={{ alignItems:'flex-end' }}>
                    <Text theme='h1' color={Colors.text.action}>{players_generated}</Text>
                    <Text theme='description' style={{ marginTop:3 }}>Players Generated</Text>
                </View>
            </Button>
        )
    }

    const renderMembers = (data:{ item:CompanyMemberProps, index:number }) => {
        const player = players.find(p => p.player_id == data.item.player_id);
        if(!player){ return <></> }
        return (
            <View transparent type='row' style={{ padding:10, margin:4, minWidth:300, flexGrow:1, borderBottomWidth:1, borderColor:Colors.borders.light }}>
                <Image
                    source={{ uri: player.profile_pic }}
                    style={{ height:40, width:40, borderRadius:100 }}
                    resizeMode='cover'
                />
                <View transparent style={{ flex:1, marginLeft:10 }}>
                    <Text theme='h1'>{player.first_name} {player.last_name}</Text>
                    <Text theme='description' style={{ marginTop:3 }}>{data.item.role}</Text>
                </View>
            </View>
        )
    }

    
    const renderSections = (data:{ item:string, index:number }) => {
        switch(data.item){
            case 'top_row':
                if(!company){ return <></> }
                //Top Rows will consist of all the metrics
                const total_revenue = company_player_stats.reduce((a,b) => a + b.revenue, 0);
                return (
                    <View transparent style={{ flexDirection:'row', flexWrap:'wrap' }}>
                        
                        <View float nativeID='company_info' style={{ flexGrow:1, flexBasis:1, minWidth:200, margin:10 }}>
                            <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:10, borderTopRightRadius:8, borderTopLeftRadius:8 }}>
                              <View transparent style={{ flex:1 }}>
                                <Text theme='h1'>Company Information</Text>
                              </View>
                            </View>
                            <View type='body' transparent style={{  padding:10 }}>
                                <View transparent type='row'>
                                    <View transparent style={{ flex:1 }}>
                                        <Text theme='h1' size={32}>{company.company_name}</Text>
                                        <Text theme='h2' style={{ marginTop:3, flexWrap:'wrap', flexShrink:1 }} size={14}>{company.company_description}</Text>
                                    </View>
                                    <ImageUploader onFinishUpload={(obj) => console.log(obj.secure_url)} public_id={`company_logo_${company_id}_${Math.random()*100000}`}>
                                        <Image
                                            source={{ uri: company.company_image?.url }}
                                            style={{ height:75, width:75 }}
                                            resizeMode='contain'
                                        />
                                    </ImageUploader>
                                </View>
                            </View>
                        </View>
                        <View float nativeID='player_count' style={{ flexGrow:1, minWidth: 250, margin:10 }}>
                            <View type='footer' style={{ flexDirection:'row', alignItems:'center', padding:10, borderTopRightRadius:8, borderTopLeftRadius:8 }}>
                              <View transparent style={{ flex:1 }}>
                                <Text theme='h1' color={Colors.text.success}>Total Revenue</Text>
                              </View>
                            </View>
                            <View transparent style={{ flexGrow:Platform.OS == 'web' ? 1 : undefined, justifyContent:'center', alignItems:'center', padding:10 }}>
                                <Text theme='h1' size={60} color={Colors.text.success}>${total_revenue.toFixed(2)}</Text>
                            </View>
                        </View>

                        <View float nativeID='company_details' style={{ flexGrow:1, flexBasis:1, minWidth: 200, margin:10 }}>
                            <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:10, borderTopRightRadius:8, borderTopLeftRadius:8 }}>
                              <View transparent style={{ flex:1 }}>
                                <Text theme='h1'>Company Members</Text>
                              </View>
                              {false ?
                              <Button
                                type='success'
                                title='NEW MEMBER'
                                style={{ padding:10 }}
                                onPress={() => console.log('hey')}
                              />
                              :<></>}
                            </View>
                            <View transparent style={{ flexDirection:'row', flexWrap:'wrap'}}>
                                {company_members.filter(cm => !cm.hidden).map((m,i) => {
                                    return renderMembers({ item: m, index:i })
                                })}
                            </View>
                        </View>

                         <View float nativeID='company_competitions' style={{ flexGrow:1, flexBasis:1, minWidth:300, margin:10 }}>
                            <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:10, borderTopRightRadius:8, borderTopLeftRadius:8 }}>
                              <View transparent style={{ flex:1 }}>
                                <Text theme='h1'>Keys</Text>
                                <Text theme='description' style={{ marginTop:3 }}>Your active API Keys</Text>
                              </View>
                              {false ?
                              <Button
                                title='REQUEST'
                                type='success'
                                style={{ padding:10 }}
                                onPress={() => console.log('CREATE A NEW KEY!!!')}
                              />
                              :<></>}
                            </View>
                            <View transparent type='body'>
                                <FlatList
                                    data={external_keys.sort((a,b) => moment(a.create_datetime).unix() - moment(b.create_datetime).unix()).slice(0,3)}
                                    key={'company_keys'}
                                    keyExtractor={item => item.external_key_id.toString()}
                                    renderItem={renderKeys}
                                />
                            </View>
                        </View>
                    </View>
                )
            case 'stats':
                const player_count = company_player_stats.reduce((a,b) => a + b.player_count, 0);
                return (
                    <View transparent style={{ flexDirection:'row', flexWrap:'wrap' }}>
                        <View float nativeID='player_count' style={{ flexGrow:1, minWidth: 250, margin:10 }}>
                            <View transparent style={{ justifyContent:'center', alignItems:'center', padding:10 }}>
                                <Text theme='h1' size={40}>{player_count}</Text>
                            </View>
                            <View type='footer' style={{ flexDirection:'row', alignItems:'center', padding:10, borderBottomRightRadius:8, borderBottomLeftRadius:8 }}>
                              <View transparent style={{ flex:1 }}>
                                <Text theme='h1' color={Colors.text.success}>Users Generated</Text>
                              </View>
                            </View>
                        </View>
                        <View float nativeID='company_referrals' style={{ flexGrow:4,  minWidth:300, margin:10 }}>
                            <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:10, borderTopRightRadius:8, borderTopLeftRadius:8 }}>
                              <View transparent style={{ flex:1 }}>
                                <Text theme='h1'>Players Generated</Text>
                                <Text theme='description' style={{ marginTop:3 }}>See all players that have joined BettorEdge using your referral code</Text>
                              </View>
                            </View>
                            <View style={{ padding:10 }}>
                                <SearchBox hide_search_button onChange={(text) => setPlayerSearch(text)} />
                            </View>
                           <ScrollView horizontal={true} contentContainerStyle={{ flexGrow: 1 }}>
                                <View transparent type='body' style={{ flex:1 }}>
                                    <FlatList
                                    data={filtered_players}
                                    key={'company_players'}
                                    keyExtractor={item => item.player_id.toString()}
                                    renderItem={renderPlayers}
                                    />
                                </View>
                            </ScrollView>
                            <Pagination offset={company_player_local_offset} onNext={() => handleNext()} onPrevious={() => handlePrev()} />
                        </View>
                        <View />
                    </View>
                )
            case 'referrals':
                return (
                    <View transparent style={{ flexDirection:'row', flexWrap:'wrap' }}>
                        <View float nativeID='company_referrals' style={{ flexGrow:4, flexBasis:4, minWidth:300, margin:10 }}>
                            <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:10, borderTopRightRadius:8, borderTopLeftRadius:8 }}>
                              <View transparent style={{ flex:1 }}>
                                <Text theme='h1'>Referral Codes</Text>
                                <Text theme='description' style={{ marginTop:3 }}>Active Referral Codes</Text>
                              </View>
                              <View transparent style={{ padding:10 }}>
                                <Text theme='h1' color={Colors.text.action}>{player_referrals.length}</Text>
                              </View>
                              <Button
                                title='CREATE'
                                type='success'
                                style={{ padding:10 }}
                                onPress={() => onManageReferral()}
                              />
                            </View>
                            <View style={{ padding:10 }}>
                                <SearchBox hide_search_button onChange={(text) => setReferralSearch(text)} />
                            </View>
                            <View transparent type='body'>
                                <FlatList
                                    data={filtered_referrals.sort((a,b) => moment(a.create_datetime).unix() - moment(b.create_datetime).unix())}
                                    key={'company_referrals'}
                                    keyExtractor={item => item.player_referral_id.toString()}
                                    renderItem={renderReferrals}
                                />
                            </View>
                        </View>
                    </View>
                )
            case 'second_row':
                const active_embeds = company_embeds.length
                return (
                    <View transparent style={{ flexDirection:'row', flexWrap:'wrap' }}>
                        
                         <View float nativeID='active_embeds' style={{ flexGrow:1, minWidth: 250, margin:10 }}>
                            <View transparent style={{ justifyContent:'center', alignItems:'center', padding:10 }}>
                                <Text theme='h1' size={40}>{active_embeds}</Text>
                            </View>
                            <View type='footer' style={{ flexDirection:'row', alignItems:'center', padding:10, borderBottomRightRadius:8, borderBottomLeftRadius:8 }}>
                              <View transparent style={{ flex:1 }}>
                                <Text theme='h1' color={Colors.text.success}>Active Embeds</Text>
                              </View>
                            </View>
                        </View>
                        
                        <View float nativeID='company_competitions' style={{ flexGrow:4, flexBasis:4, minWidth:300, margin:10 }}>
                            <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:10, borderTopRightRadius:8, borderTopLeftRadius:8 }}>
                              <View transparent style={{ flex:1 }}>
                                <Text theme='h1'>Embeds</Text>
                                <Text theme='description' style={{ marginTop:3 }}>Active Embeds</Text>
                              </View>
                              <Button
                                title='CREATE'
                                type='success'
                                style={{ padding:10 }}
                                onPress={() => onCreateEmbed()}
                              />
                            </View>
                            <View transparent type='body'>
                                <FlatList
                                    data={company_embeds.sort((a,b) => moment(a.create_datetime).unix() - moment(b.create_datetime).unix())}
                                    key={'company_embeds'}
                                    keyExtractor={item => item.company_embed_id.toString()}
                                    renderItem={renderEmbeds}
                                />
                            </View>
                        </View>
                    </View>
                )
            case 'third_row':
                const active_engagements = competitions.length + squares_competitions.length + bracket_competitions.length + flash_campaigns.length
                return (
                    <View transparent style={{ flexDirection:'row', flexWrap:'wrap' }}>
                        
                        <View float nativeID='active_engagements' style={{ flexGrow:1, minWidth: 250, margin:10 }}>
                            <View transparent style={{ justifyContent:'center', alignItems:'center', padding:10 }}>
                                <Text theme='h1' size={40}>{active_engagements}</Text>
                            </View>
                            <View type='footer' style={{ flexDirection:'row', alignItems:'center', padding:10, borderBottomRightRadius:8, borderBottomLeftRadius:8 }}>
                              <View transparent style={{ flex:1 }}>
                                <Text theme='h1' color={Colors.text.success}>Active Engagements</Text>
                              </View>
                            </View>
                        </View>
                        
                        <View float nativeID='company_competitions' style={{ flexGrow:1, flexBasis:1, minWidth:300, margin:10 }}>
                            <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:10, borderTopRightRadius:8, borderTopLeftRadius:8 }}>
                              <View transparent style={{ flex:1 }}>
                                <Text theme='h1'>Competitions</Text>
                                <Text theme='description' style={{ marginTop:3 }}>Pick-ems / Wager competitions that are currently active</Text>
                              </View>
                              <Button
                                title='CREATE'
                                type='success'
                                style={{ padding:10 }}
                                onPress={() => onCreateEngagement('competition')}
                              />
                            </View>
                            <View transparent type='body'>
                                <FlatList
                                    data={competitions.sort((a,b) => moment(a.scheduled_datetime).unix() - moment(b.scheduled_datetime).unix())}
                                    key={'company_comps'}
                                    keyExtractor={item => item.competition_id.toString()}
                                    renderItem={renderCompetitions}
                                />
                            </View>
                        </View>
                        <View float nativeID='company_squares' style={{ flexGrow:1, flexBasis:1, minWidth:300, margin:10 }}>
                            <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:10, borderTopRightRadius:8, borderTopLeftRadius:8 }}>
                              <View transparent style={{ flex:1 }}>
                                <Text theme='h1'>Squares</Text>
                                <Text theme='description' style={{ marginTop:3 }}>Bid on squares and win at the end of each quarter</Text>
                              </View>
                              <Button
                                title='CREATE'
                                type='success'
                                style={{ padding:10 }}
                                onPress={() => onCreateEngagement('squares')}
                              />
                            </View>
                            <View transparent type='body'>
                                <FlatList
                                    data={squares_competitions.sort((a,b) => moment(a.begin_datetime).unix() - moment(b.begin_datetime).unix())}
                                    key={'company_comps'}
                                    keyExtractor={item => item.sq_comp_id.toString()}
                                    renderItem={renderSquares}
                                />
                            </View>
                        </View>

                        <View float nativeID='company_brackets' style={{ flexGrow:1, flexBasis:1, minWidth:300, margin:10 }}>
                            <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:10, borderTopRightRadius:8, borderTopLeftRadius:8 }}>
                              <View transparent style={{ flex:1 }}>
                                <Text theme='h1'>Brackets</Text>
                                <Text theme='description' style={{ marginTop:3 }}>Active Bracket Competitions</Text>
                              </View>
                              <Button
                                title='CREATE'
                                type='success'
                                style={{ padding:10 }}
                                onPress={() => onCreateEngagement('bracket')}
                              />
                            </View>
                            <View transparent type='body'>
                                <FlatList
                                    data={bracket_competitions.sort((a,b) => moment(a.scheduled_datetime).unix() - moment(b.scheduled_datetime).unix())}
                                    key={'company_comps'}
                                    keyExtractor={item => item.bracket_competition_id.toString()}
                                    renderItem={renderBrackets}
                                />
                            </View>
                        </View>

                         <View float nativeID='company_flash' style={{ flexGrow:1, flexBasis:1, minWidth:300, margin:10 }}>
                            <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:10, borderTopRightRadius:8, borderTopLeftRadius:8 }}>
                              <View transparent style={{ flex:1 }}>
                                <Text theme='h1'>Flash Market Campaigns</Text>
                                <Text theme='description' style={{ marginTop:3 }}>Active Flash Campaigns</Text>
                              </View>
                              <Button
                                title='CREATE'
                                type='success'
                                style={{ padding:10 }}
                                onPress={() => onCreateEngagement('flash')}
                              />
                            </View>
                            <View transparent type='body'>
                                <FlatList
                                    data={flash_campaigns.sort((a,b) => moment(a.create_datetime).unix() - moment(b.create_datetime).unix())}
                                    key={'company_flash'}
                                    keyExtractor={item => item.poll_campaign_id.toString()}
                                    renderItem={renderFlashCampaigns}
                                />
                            </View>
                        </View>

                    </View>
                )
            default: return <></>
        }
    }

    return (
        <View style={{ flex:1 }}>
            <FlatList
                data={sections}
                refreshing={loading}
                onRefresh={() => getActiveEngagementData()}
                key={'partner_portal_sections'}
                keyExtractor={item => item}
                renderItem={renderSections}
            />
        </View>
    )
}

export default PartnerPortal